import { filterByQuery, sortVariables } from '@/util/sort';
import SearchField from '@/components/common/SearchField';
import { useApplication } from '@/hooks/useApplication';
import { useSubroutine } from '@/hooks/useSubroutine';
import { SearchBlocksItem } from '@/types/search';
import { Variable, VariableScope, VariableSortType } from '@/types/variable';
import { findSubroutine } from '@/util/application';
import { variableCustomTooltip, variableSearchOptions } from '@/util/variables';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import { FC, useEffect, useState } from 'react';
import { useLocalStorage } from 'usehooks-ts';
import VariableManager from './VariableManager';

type VariableDialogProps = {
  open: boolean;
  scope: VariableScope;
  onClose: () => void;
  onVariableSearch: (searchValue: SearchBlocksItem) => void;
};

const VariableDialog: FC<VariableDialogProps> = (props) => {
  const { application, setApplication } = useApplication();
  const { selectedSubroutineId } = useSubroutine();

  const [appVars, setAppVars] = useState<Variable[]>([]);
  const [sortBy, setSortBy] = useLocalStorage<VariableSortType>('variableSort', 'date');
  const [invert, setInvert] = useState<boolean>(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [focusSearch, setFocusSearch] = useState(true);

  const currentSubroutine = findSubroutine(application.subroutines, selectedSubroutineId);

  const getVariables = () => {
    if (props.scope !== 'local') {
      return application.variables[props.scope];
    }

    if (selectedSubroutineId && application.variables[props.scope][selectedSubroutineId]) {
      return application.variables[props.scope][selectedSubroutineId];
    }

    return [];
  };

  const handleClose = () => {
    setFocusSearch(true);
    props.onClose();
  };

  useEffect(() => {
    setSearchQuery('');

    const newVariables = getVariables();
    setAppVars(newVariables);
  }, [props.open, application.variables]);

  return (
    <Dialog open={props.open} onClose={handleClose} fullWidth maxWidth='sm'>
      <DialogTitle>
        {`Variables - `}
        <code>
          {props.scope}
          {currentSubroutine && `: ${currentSubroutine.label}`}
        </code>
        {appVars?.length > 2 && (
          <SearchField
            sortBy={sortBy}
            invert={invert}
            onSort={(changeSortBy, reverse) => {
              changeSortBy && setSortBy(changeSortBy as VariableSortType);
              reverse && setInvert(!invert);
            }}
            searchOptions={variableSearchOptions}
            searchQuery={searchQuery}
            setSearchQuery={(query) => setSearchQuery(query)}
            customTooltip={variableCustomTooltip}
            autoFocus={focusSearch}
          />
        )}
      </DialogTitle>
      <DialogContent>
        <VariableManager
          scope={props.scope}
          currentSubroutine={currentSubroutine}
          query={searchQuery}
          variables={appVars}
          filteredVars={sortVariables(filterByQuery(appVars, searchQuery), sortBy, invert)}
          removeSearchFocus={() => setFocusSearch(false)}
          onVariableSearch={(searchValue) => {
            props.onVariableSearch(searchValue);
            handleClose();
          }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={() => handleClose()} color='primary'>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default VariableDialog;
